import { MODULE_CONFIGS } from '../context/ModuleContext';

/**
 * IMPORTANT: These utilities use static fallback configurations.
 * For dynamic application IDs fetched from the API, use the ModuleContext methods instead.
 * These utilities are primarily for service functions that can't use React hooks.
 */

/**
 * Get the application ID for a specific module (uses fallback configs)
 */
export const getApplicationIdForModule = (moduleKey) => {
  const config = MODULE_CONFIGS[moduleKey];
  if (!config) {
    console.warn(`Module configuration not found for: ${moduleKey}, using default`);
    return getDefaultApplicationId();
  }
  return config.applicationId;
};

/**
 * Get the default application ID (falls back to env variable)
 */
export const getDefaultApplicationId = () => {
  const defaultId = import.meta.env.VITE_APPLICATION_ID;
  if (!defaultId) {
    console.warn('VITE_APPLICATION_ID environment variable not set, using fallback');
    return 'default-application-id';
  }
  return defaultId;
};

/**
 * Get application ID with fallback to default
 */
export const getApplicationIdWithFallback = (moduleKey) => {
  if (moduleKey) {
    try {
      return getApplicationIdForModule(moduleKey);
    } catch (error) {
      console.warn(`Failed to get application ID for module ${moduleKey}, using default:`, error);
      return getDefaultApplicationId();
    }
  }
  return getDefaultApplicationId();
};

/**
 * Check if a module has a specific application ID configured
 */
export const hasCustomApplicationId = (moduleKey) => {
  const config = MODULE_CONFIGS[moduleKey];
  if (!config) return false;
  
  const defaultId = getDefaultApplicationId();
  return config.applicationId !== defaultId && !config.applicationId.startsWith('default-');
};

/**
 * Get all application IDs for debugging
 */
export const getAllApplicationIds = () => {
  const result = {};
  
  Object.keys(MODULE_CONFIGS).forEach((key) => {
    const moduleKey = key;
    result[moduleKey] = getApplicationIdForModule(moduleKey);
  });
  
  return result;
};

/**
 * Validate that all modules have valid application IDs
 */
export const validateModuleApplicationIds = () => {
  const allIds = getAllApplicationIds();
  let isValid = true;
  
  Object.entries(allIds).forEach(([module, id]) => {
    if (!id || id.trim() === '') {
      console.error(`Invalid application ID for module ${module}: ${id}`);
      isValid = false;
    }
  });
  
  return isValid;
}; 